/**
 * Shared last-message → short preview-text formatting, used everywhere a
 * contact's most recent message needs to show as a compact one-liner: the
 * Inbox chat list (MessageCard.jsx) and the Dashboard's Contacts panel
 * (ContactRow.jsx). Extracted 2026-09-09 after ContactRow.jsx's own
 * (much thinner) copy of this logic had no handling at all for most
 * message types — a location message rendered its raw
 * `{"latitude":...,"longitude":...}` JSON straight into the row instead of
 * "Location" (Rahim: "it shows weird latitude code"). Media messages
 * (image/video/audio/document), shared contacts, product-catalog orders,
 * WhatsApp-Ad referrals, and collages had the exact same class of bug there
 * — any of them would have dumped a raw URL or JSON blob into the preview.
 *
 * Keeping ONE implementation means a new message type only needs handling
 * added once, not once per surface that happens to preview messages.
 */

/**
 * Classifies a last-message's real type, going past a generic messageType
 * like "chat" by sniffing the actual content URL for images/video/audio/
 * documents dumped under a generic type by some webhook payloads.
 * @param {{messageType?: string, type?: string, content?: string, body?: string, message?: string}} lastMessage
 * @returns {string}
 */
export function classifyLastMessageType(lastMessage) {
  if (!lastMessage) return "text";
  const rawMt = lastMessage.messageType || lastMessage.type || "text";
  const content = String(lastMessage.content || lastMessage.body || lastMessage.message || "").trim();
  if (!/^https?:\/\//i.test(content)) return rawMt;
  const low = content.toLowerCase();
  if (/\.(jpe?g|png|gif|webp)(\?|#|$)/i.test(low)) return "image";
  if (/\.(mp4|webm|mov)(\?|#|$)/i.test(low)) return "video";
  if (/\.(mp3|wav|ogg|m4a)(\?|#|$)/i.test(low)) return "audio";
  if (/\/(document|documents)\//i.test(low) && /\.(bin|pdf|doc|docx|xls|xlsx|ppt|pptx|txt)(\?|#|$)/i.test(low)) {
    return "document";
  }
  if (/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt)(\?|#|$)/i.test(low)) return "document";
  return rawMt;
}

/**
 * Formats a last-message into a short, human-readable preview label (e.g.
 * "Location", "🛒 Product Order", "Image") instead of dumping raw
 * JSON/media-URL content. Deliberately does NOT resolve special sender
 * names (Automated/Broadcast/Partner API/etc.) or filter out log-only rows
 * (assignment/date/closechat) -- those are Inbox-specific concerns; callers
 * that need that layer (MessageCard.jsx) apply it on top of this using the
 * raw `content` this function also uses for plain text messages.
 * @param {object} lastMessage
 * @returns {string}
 */
export function formatLastMessagePreview(lastMessage) {
  if (!lastMessage) return "";
  const messageType = classifyLastMessageType(lastMessage);
  const content = lastMessage.content || lastMessage.body || lastMessage.message || "";

  // Group event system-log types (2026-09-26 follow-up, WhatsApp Groups plan): unlike
  // assignment/closechat/blockcontact (CRM-only concepts, no WhatsApp-visible equivalent,
  // handled by MessageCard.jsx's own LOG_ONLY_MESSAGE_TYPES layer), these are real WhatsApp
  // group events -- worded the same short way ChatBody.jsx's own centered log pill already
  // does for the full chat view, just without the trailing timestamp (the list row already
  // shows one of its own).
  if (
    messageType === "group_created" ||
    messageType === "group_participants_update" ||
    messageType === "group_subject_update" ||
    messageType === "group_description_update" ||
    messageType === "group_settings_update" ||
    messageType === "group_join_request"
  ) {
    let parsed = null;
    try { parsed = JSON.parse(content || "{}"); } catch { /* not JSON, ignore */ }
    if (messageType === "group_created") {
      if (parsed?.addedBy) return `${parsed.addedBy} added you to ${parsed.subject || "a group"}`;
      return parsed?.subject ? `${parsed.subject} is now in your inbox` : "Now in your inbox";
    }
    if (messageType === "group_participants_update") {
      const participantAction = parsed?.participantAction;
      const participants = Array.isArray(parsed?.participants) ? parsed.participants : [];
      const nameOf = (p) => p?.name || p?.phoneNumberID || p?.jid || "someone";
      const authorName = parsed?.author?.name || parsed?.author?.phoneNumberID || null;
      const namesText = participants.length > 0 ? participants.map(nameOf).join(", ") : "someone";
      const verb =
        participantAction === "add" ? "added" :
        participantAction === "remove" ? "removed" :
        participantAction === "promote" ? "made group admin" :
        participantAction === "demote" ? "removed as group admin" : "updated";
      if (
        participantAction === "remove" &&
        participants.length === 1 &&
        parsed?.author?.phoneNumberID &&
        participants[0]?.phoneNumberID === parsed.author.phoneNumberID
      ) {
        return `${authorName || "Someone"} left`;
      }
      return authorName
        ? `${authorName} ${verb} ${namesText}`
        : `${namesText} ${participantAction === "remove" ? "left or was removed" : verb}`;
    }
    const authorName = parsed?.author?.name || parsed?.author?.phoneNumberID || "Someone";
    if (messageType === "group_join_request") {
      const who = parsed?.participant?.name || parsed?.participant?.phoneNumberID || "Someone";
      if (parsed?.requestAction === "revoked") return `${who} cancelled their request to join`;
      if (parsed?.requestAction === "rejected") {
        const by = parsed?.author?.name || parsed?.author?.phoneNumberID;
        return by ? `${by} rejected ${who}'s request to join` : `${who}'s request to join was rejected`;
      }
      return `${who} requested to join this group`;
    }
    if (messageType === "group_settings_update") {
      const value = parsed?.value;
      if (parsed?.setting === "joinApprovalMode") {
        return `${authorName} turned ${value ? "on" : "off"} admin approval to join this group`;
      }
      const change =
        parsed?.setting === "memberAddMode"
          ? (value === "all_member_add" ? "allow all members to add others" : "allow only admins to add others")
          : parsed?.setting === "announce"
            ? (value ? "allow only admins to send messages" : "allow all members to send messages")
            : parsed?.setting === "restrict"
              ? (value ? "allow only admins to edit group info" : "allow all members to edit group info")
              : "a group setting";
      return `${authorName} changed the group settings to ${change}`;
    }
    if (messageType === "group_subject_update") {
      return `${authorName} changed the group name${parsed?.subject ? ` to ${parsed.subject}` : ""}`;
    }
    return `${authorName} changed the group description`;
  }

  if (typeof content === "string" && content.trim().startsWith("{")) {
    try {
      const p = JSON.parse(content);
      if (Array.isArray(p?.contacts) && p.contacts.length > 0 && p.contacts[0] && (p.contacts[0].name || p.contacts[0].phones)) {
        return "Contact";
      }
      const lat = Number(p?.latitude);
      const lng = Number(p?.longitude);
      if (!Number.isNaN(lat) && !Number.isNaN(lng) && !p?.productId && !p?.catalogId && !p?.components && !p?.catalogType && !p?.productData) {
        return "Location";
      }
      if (p && typeof p === "object" && (p.catalogType || p.productData)) {
        return "🛒 Product Order";
      }
    } catch {
      // not JSON, fall through
    }
  }

  if (messageType === "text") return content;
  if (messageType === "audio") return "Audio";
  if (messageType === "document") return "Document";
  if (messageType === "image") return "Image";
  if (messageType === "video") return "Video";
  if (messageType === "location") return "Location";
  if (messageType === "contacts") return "Contact";
  if (
    messageType === "list" ||
    lastMessage.messageType === "list" ||
    (typeof content === "string" && content.trim().startsWith("{") && (content.includes('"header"') || content.includes("header")))
  ) return "List Message";
  if (messageType === "template") return "Template Message";
  if (messageType === "catalog" || messageType === "catalog_purchase") return "🛒 Product Order";
  if (messageType === "ctwa_referral") return "📢 Message from WhatsApp Ad";
  if (messageType === "imageCollage") return "Image Collage";
  if (messageType === "videoCollage") return "Video Collage";
  if (messageType) return messageType.charAt(0).toUpperCase() + messageType.slice(1);
  return content;
}
